/**
 * Order, fulfilment and line statuses (03-modules.md §4, 04-data-model.md
 * `orders.order.status`, `fulfilment_status`, `orders.order_line.status`).
 *
 * The two order-level fields together are the product page's "19-state
 * lifecycle": `status` is the commercial life of the order (taking, billing,
 * paying), `fulfilmentStatus` is the physical one (kitchen, pass, rider).
 */

export const ORDER_TYPES = ['dine_in', 'takeaway', 'delivery', 'aggregator'] as const;
/** `qr_dine_in` in 03-modules.md is `dine_in` with `source = 'qr'` (04-data-model.md check). */
export type OrderType = (typeof ORDER_TYPES)[number];

export const ORDER_STATUSES = [
  'draft',
  'pending_acceptance',
  'accepted',
  'open',
  'billed',
  'partially_paid',
  'settled',
  'closed',
  'rejected',
  'cancelled',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** No transition leaves these. */
export const TERMINAL_ORDER_STATUSES: ReadonlySet<OrderStatus> = new Set([
  'closed',
  'rejected',
  'cancelled',
]);

/** Statuses an order may be created in (`openOrder`). */
export const INITIAL_ORDER_STATUSES = ['draft', 'pending_acceptance', 'open'] as const;
export type InitialOrderStatus = (typeof INITIAL_ORDER_STATUSES)[number];

export const FULFILMENT_STATUSES = [
  'not_started',
  'in_kitchen',
  'ready',
  'served',
  'picked_up',
  'handed_to_rider',
  'out_for_delivery',
  'delivered',
  'delivery_failed',
  'returned',
] as const;
export type FulfilmentStatus = (typeof FULFILMENT_STATUSES)[number];

/**
 * How far along each fulfilment status is. Automatic fulfilment changes (from
 * the kitchen) only move within ranks 0..2 (and to `served` for dine-in).
 */
export const FULFILMENT_RANK: Readonly<Record<FulfilmentStatus, number>> = {
  not_started: 0,
  in_kitchen: 1,
  ready: 2,
  served: 3,
  picked_up: 3,
  handed_to_rider: 3,
  out_for_delivery: 4,
  delivered: 5,
  delivery_failed: 5,
  returned: 6,
};

const KITCHEN_PATH: readonly (readonly [FulfilmentStatus, FulfilmentStatus])[] = [
  ['not_started', 'in_kitchen'],
  ['not_started', 'ready'],
  ['in_kitchen', 'ready'],
  // Recall from the pass: the kitchen takes a ready order back.
  ['ready', 'in_kitchen'],
];

function handOver(to: FulfilmentStatus): (readonly [FulfilmentStatus, FulfilmentStatus])[] {
  return [
    ['not_started', to],
    ['in_kitchen', to],
    ['ready', to],
  ];
}

/**
 * Allowed manual fulfilment moves per order type (`setFulfilmentStatus`):
 *
 *   dine_in:              not_started → in_kitchen → ready → served
 *   takeaway:             not_started → in_kitchen → ready → picked_up
 *   delivery, aggregator: not_started → in_kitchen → ready → handed_to_rider
 *                         → out_for_delivery → delivered | delivery_failed → returned
 *
 * Forward skips are allowed (a bottle of water is served without a KOT; a rider
 * update may jump `handed_to_rider → delivered`); the only backward move is the
 * recall `ready → in_kitchen`.
 */
export const FULFILMENT_TRANSITIONS: Readonly<
  Record<OrderType, readonly (readonly [FulfilmentStatus, FulfilmentStatus])[]>
> = {
  dine_in: [...KITCHEN_PATH, ...handOver('served')],
  takeaway: [...KITCHEN_PATH, ...handOver('picked_up')],
  delivery: deliveryPath(),
  aggregator: deliveryPath(),
};

function deliveryPath(): (readonly [FulfilmentStatus, FulfilmentStatus])[] {
  return [
    ...KITCHEN_PATH,
    ...handOver('handed_to_rider'),
    ['handed_to_rider', 'out_for_delivery'],
    ['handed_to_rider', 'delivered'],
    ['handed_to_rider', 'delivery_failed'],
    ['out_for_delivery', 'delivered'],
    ['out_for_delivery', 'delivery_failed'],
    ['delivery_failed', 'returned'],
  ];
}

export function canMoveFulfilment(
  type: OrderType,
  from: FulfilmentStatus,
  to: FulfilmentStatus,
): boolean {
  return FULFILMENT_TRANSITIONS[type].some(([f, t]) => f === from && t === to);
}

/**
 * Fulfilment statuses at which a settled order may be closed. Dine-in closes at
 * any fulfilment: settling is the guest leaving, and many outlets never mark
 * dishes served. Aggregator orders close once handed to the platform's rider
 * (we may get no further updates).
 */
export const CLOSABLE_FULFILMENT: Readonly<Record<OrderType, ReadonlySet<FulfilmentStatus>>> = {
  dine_in: new Set(FULFILMENT_STATUSES),
  takeaway: new Set(['picked_up']),
  delivery: new Set(['delivered', 'returned']),
  aggregator: new Set(['handed_to_rider', 'out_for_delivery', 'delivered', 'returned']),
};

export const LINE_STATUSES = ['pending', 'sent', 'preparing', 'ready', 'served', 'void'] as const;
/**
 * `pending`: not yet in the kitchen (a KOT may be printing: `pendingKotId`).
 * `sent`: KOT print confirmed. `void` is terminal. Complimentary is a flag on
 * the line, not a status (it changes the price, not the kitchen's view).
 */
export type LineStatus = (typeof LINE_STATUSES)[number];

/** The kitchen's forward moves (KDS bump, pass, captain). `void` is its own action. */
export const LINE_TRANSITIONS: Readonly<Record<LineStatus, readonly LineStatus[]>> = {
  pending: ['sent'],
  sent: ['preparing', 'ready', 'served'],
  preparing: ['ready', 'served'],
  // ready → preparing is a recall (`recallLines`), not a bump.
  ready: ['served'],
  served: [],
  void: [],
};
